import { View } from "react-native";
import Svg, { Defs, RadialGradient, Stop, Ellipse } from "react-native-svg";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
  Easing,
} from "react-native-reanimated";
import { useEffect, useId } from "react";

interface GradientOrbProps {
  size?: number;
}

export function GradientOrb({ size = 100 }: GradientOrbProps) {
  const uid = useId().replace(/:/g, "");
  const rotation1 = useSharedValue(0);
  const rotation2 = useSharedValue(0);
  const rotation3 = useSharedValue(0);

  useEffect(() => {
    rotation1.value = withRepeat(
      withTiming(360, { duration: 12000, easing: Easing.linear }),
      -1,
      false,
    );
    rotation2.value = withRepeat(
      withTiming(-360, { duration: 18000, easing: Easing.linear }),
      -1,
      false,
    );
    rotation3.value = withRepeat(
      withTiming(360, { duration: 25000, easing: Easing.linear }),
      -1,
      false,
    );
  }, [rotation1, rotation2, rotation3]);

  const layer1Style = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation1.value}deg` }],
  }));

  const layer2Style = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation2.value}deg` }],
  }));

  const layer3Style = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation3.value}deg` }],
  }));

  const id = (name: string) => `${uid}${name}`;
  const ref = (name: string) => `url(#${uid}${name})`;

  const layerAbsolute = {
    position: "absolute" as const,
    width: size,
    height: size,
  };

  return (
    <View style={{ width: size, height: size }}>
      <Animated.View
        style={[
          {
            ...layerAbsolute,
            // @ts-ignore
            filter: "blur(8px)",
          },
          layer1Style,
        ]}
      >
        <Svg width={size} height={size} viewBox="0 0 100 100">
          <Defs>
            <RadialGradient
              id={id("magentaBlob1")}
              cx="25%"
              cy="30%"
              rx="40%"
              ry="45%"
            >
              <Stop offset="0%" stopColor="#db2777" stopOpacity="0.8" />
              <Stop offset="40%" stopColor="#a21caf" stopOpacity="0.5" />
              <Stop offset="100%" stopColor="#581c87" stopOpacity="0" />
            </RadialGradient>
            <RadialGradient
              id={id("tealBlob1")}
              cx="75%"
              cy="70%"
              rx="35%"
              ry="38%"
            >
              <Stop offset="0%" stopColor="#2dd4bf" stopOpacity="0.7" />
              <Stop offset="50%" stopColor="#14b8a6" stopOpacity="0.4" />
              <Stop offset="100%" stopColor="#0d9488" stopOpacity="0" />
            </RadialGradient>
          </Defs>
          <Ellipse cx="30" cy="35" rx="38" ry="42" fill={ref("magentaBlob1")} />
          <Ellipse cx="70" cy="65" rx="32" ry="35" fill={ref("tealBlob1")} />
        </Svg>
      </Animated.View>

      <Animated.View
        style={[
          {
            ...layerAbsolute,
            // @ts-ignore
            filter: "blur(4px)",
          },
          layer2Style,
        ]}
      >
        <Svg width={size} height={size} viewBox="0 0 100 100">
          <Defs>
            <RadialGradient
              id={id("orangeBlob2")}
              cx="70%"
              cy="35%"
              rx="38%"
              ry="42%"
            >
              <Stop offset="0%" stopColor="#fb923c" stopOpacity="0.85" />
              <Stop offset="35%" stopColor="#f97316" stopOpacity="0.55" />
              <Stop offset="100%" stopColor="#c2410c" stopOpacity="0" />
            </RadialGradient>
            <RadialGradient
              id={id("violetBlob2")}
              cx="30%"
              cy="65%"
              rx="30%"
              ry="32%"
            >
              <Stop offset="0%" stopColor="#c4b5fd" stopOpacity="0.65" />
              <Stop offset="50%" stopColor="#a78bfa" stopOpacity="0.35" />
              <Stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
            </RadialGradient>
          </Defs>
          <Ellipse cx="65" cy="38" rx="36" ry="40" fill={ref("orangeBlob2")} />
          <Ellipse cx="35" cy="62" rx="28" ry="30" fill={ref("violetBlob2")} />
        </Svg>
      </Animated.View>

      <Animated.View
        style={[
          {
            ...layerAbsolute,
            // @ts-ignore
            filter: "blur(3px)",
          },
          layer3Style,
        ]}
      >
        <Svg width={size} height={size} viewBox="0 0 100 100">
          <Defs>
            <RadialGradient
              id={id("goldBlob3")}
              cx="60%"
              cy="55%"
              rx="32%"
              ry="35%"
            >
              <Stop offset="0%" stopColor="#fcd34d" stopOpacity="0.8" />
              <Stop offset="40%" stopColor="#fbbf24" stopOpacity="0.5" />
              <Stop offset="100%" stopColor="#d97706" stopOpacity="0" />
            </RadialGradient>
            <RadialGradient
              id={id("coralBlob3")}
              cx="40%"
              cy="45%"
              rx="25%"
              ry="28%"
            >
              <Stop offset="0%" stopColor="#fda4af" stopOpacity="0.7" />
              <Stop offset="50%" stopColor="#fb7185" stopOpacity="0.4" />
              <Stop offset="100%" stopColor="#f43f5e" stopOpacity="0" />
            </RadialGradient>
          </Defs>
          <Ellipse cx="58" cy="52" rx="30" ry="33" fill={ref("goldBlob3")} />
          <Ellipse cx="42" cy="48" rx="24" ry="26" fill={ref("coralBlob3")} />
        </Svg>
      </Animated.View>

      <View
        style={{
          ...layerAbsolute,
          // @ts-ignore
          filter: "blur(5px)",
        }}
      >
        <Svg width={size} height={size} viewBox="0 0 100 100">
          <Defs>
            <RadialGradient
              id={id("hotCenter")}
              cx="50%"
              cy="50%"
              rx="20%"
              ry="22%"
            >
              <Stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
              <Stop offset="40%" stopColor="#fef3c7" stopOpacity="0.5" />
              <Stop offset="100%" stopColor="#fde68a" stopOpacity="0" />
            </RadialGradient>
            <RadialGradient
              id={id("peachWrap")}
              cx="50%"
              cy="50%"
              rx="45%"
              ry="48%"
            >
              <Stop offset="0%" stopColor="#fed7aa" stopOpacity="0.3" />
              <Stop offset="60%" stopColor="#fdba74" stopOpacity="0.15" />
              <Stop offset="100%" stopColor="#fb923c" stopOpacity="0" />
            </RadialGradient>
          </Defs>
          <Ellipse cx="50" cy="50" rx="42" ry="44" fill={ref("peachWrap")} />
          <Ellipse cx="50" cy="50" rx="20" ry="22" fill={ref("hotCenter")} />
        </Svg>
      </View>

      <View
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: size / 2,
          opacity: 0.35,
          mixBlendMode: "overlay",
          // @ts-ignore
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundSize: "60px 60px",
        }}
        pointerEvents="none"
      />
    </View>
  );
}
